type TCancelablePromiseConstructorFunction<T> = (resolve: (value?: T | PromiseLike<T>) => void, reject: (error: any) => void) => void;

export class CancelablePromise<T = any> implements PromiseLike<T> {
    promise: PromiseLike<T>;
    cancelablePromise: CancelablePromise<T>;
    thenPromise?: CancelablePromise<T>;
    isCanceled: boolean;

    constructor(func: PromiseLike<T> | TCancelablePromiseConstructorFunction<T>, thenPromise?: CancelablePromise<T>) {
        this.promise = typeof (func) === "function" ? new Promise(func) as CancelablePromise<T> : func;
        this.thenPromise = thenPromise;
        this.isCanceled = false;
        this.cancelablePromise = new Promise((resolve, reject) => {
            this.promise.then(value => {
                if (!this.isCanceled) {
                    resolve(value);
                }
            }, value => {
                if (!this.isCanceled) {
                    reject(value);
                }
            });
        }) as CancelablePromise<T>;
    }

    get [Symbol.toStringTag]() {
        return "Promise";
    }

    /* eslint-disable max-len */
    static all<T1, T2, T3, T4, T5, T6, T7, T8, T9, T10>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>, T4 | PromiseLike <T4>, T5 | PromiseLike<T5>, T6 | PromiseLike<T6>, T7 | PromiseLike<T7>, T8 | PromiseLike<T8>, T9 | PromiseLike<T9>, T10 | PromiseLike<T10>]): CancelablePromise<[T1, T2, T3, T4, T5, T6, T7, T8, T9, T10]>;
    static all<T1, T2, T3, T4, T5, T6, T7, T8, T9>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>, T4 | PromiseLike <T4>, T5 | PromiseLike<T5>, T6 | PromiseLike<T6>, T7 | PromiseLike<T7>, T8 | PromiseLike<T8>, T9 | PromiseLike<T9>]): CancelablePromise<[T1, T2, T3, T4, T5, T6, T7, T8, T9]>;
    static all<T1, T2, T3, T4, T5, T6, T7, T8>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>, T4 | PromiseLike <T4>, T5 | PromiseLike<T5>, T6 | PromiseLike<T6>, T7 | PromiseLike<T7>, T8 | PromiseLike<T8>]): CancelablePromise<[T1, T2, T3, T4, T5, T6, T7, T8]>;
    static all<T1, T2, T3, T4, T5, T6, T7>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>, T4 | PromiseLike <T4>, T5 | PromiseLike<T5>, T6 | PromiseLike<T6>, T7 | PromiseLike<T7>]): CancelablePromise<[T1, T2, T3, T4, T5, T6, T7]>;
    static all<T1, T2, T3, T4, T5, T6>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>, T4 | PromiseLike <T4>, T5 | PromiseLike<T5>, T6 | PromiseLike<T6>]): CancelablePromise<[T1, T2, T3, T4, T5, T6]>;
    static all<T1, T2, T3, T4, T5>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>, T4 | PromiseLike <T4>, T5 | PromiseLike<T5>]): CancelablePromise<[T1, T2, T3, T4, T5]>;
    static all<T1, T2, T3, T4>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>, T4 | PromiseLike <T4>]): CancelablePromise<[T1, T2, T3, T4]>;
    static all<T1, T2, T3>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>, T3 | PromiseLike<T3>]): CancelablePromise<[T1, T2, T3]>;
    static all<T1, T2>(values: [T1 | PromiseLike<T1>, T2 | PromiseLike<T2>]): CancelablePromise<[T1, T2]>;
    /* eslint-enable max-len */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    static all<T>(values: (T | PromiseLike<T>)[]): CancelablePromise<T[]>;
    static all(promises: CancelablePromise[]): CancelablePromise<any[]> {
        return new CancelablePromise((resolve, reject) => {
            Promise.all(promises).then(resolve).catch(reject);
        });
    }

    static race(promises: CancelablePromise[]): CancelablePromise {
        return new CancelablePromise((resolve, reject) => {
            Promise.race(promises).then(resolve).catch(reject);
        });
    }

    static resolve<T2>(resolveValue?: T2 | PromiseLike<T2>): CancelablePromise<T2> {
        return new CancelablePromise((resolve, reject) => {
            Promise.resolve(resolveValue).then(resolve).catch(reject);
        });
    }

    static reject(rejectValue?: any): CancelablePromise {
        return new CancelablePromise((resolve, reject) => {
            Promise.reject(rejectValue).then(resolve).catch(reject);
        });
    }

    timeout(intervalMs: number): CancelablePromise<T> {
        if (intervalMs < 0) {
            // If timeout is negative, ignore it (allows use of custom codes to enable/disable timeouts)
            return this.cancelablePromise;
        }
        let timeoutId;
        return CancelablePromise.race([
            new CancelablePromise<T>((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error(`Timed out in ${ intervalMs }ms`));
                }, intervalMs);
            }),
            new CancelablePromise<T>((resolve, reject) => {
                return this.cancelablePromise.then(args => {
                    clearTimeout(timeoutId);
                    resolve(args);
                }, err => {
                    clearTimeout(timeoutId);
                    reject(typeof err === "string" ? new Error(err) : err);
                });
            })
        ]);
    }

    cancel(): void {
        this.isCanceled = true;
        this.thenPromise && this.thenPromise.cancel && this.thenPromise.cancel();
    }

    then<TResult1 = T, TResult2 = never>(
        successCallback?: ((value: T) => TResult1 | PromiseLike<TResult1>) | undefined | null,
        errorCallback?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
    ): CancelablePromise<TResult1 | TResult2> {
        let nativePromise = this.cancelablePromise.then<TResult1, TResult2>(successCallback, errorCallback);
        return new CancelablePromise<TResult1 | TResult2>(nativePromise, this as CancelablePromise);
    }

    catch<TResult = never>(errorCallback?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null): CancelablePromise<T | TResult> {
        let nativePromise = this.cancelablePromise.catch<any>(errorCallback);
        return new CancelablePromise<T | TResult>(nativePromise, this);
    }
}